import React, { useState, useEffect, useMemo } from 'react';
import { IEditorProps } from './props-interfaces';
import { config } from 'config';
import _ from 'lodash';
import { ServiceConfig, ServiceFull, ServiceDetail } from 'data/service-spec';
import urlFileIcon from '../images/urlFile.svg';
import jsonFileIcon from '../images/jsonFile.svg';
import authenticationIcon from '../images/authentication.svg';
import servicesIcon from '../images/grid.svg';
import processIcon from '../images/process.svg';
import paymentsIcon from '../images/payments.svg';
import overlayIcon from '../images/overlay.svg';
import { SchemaSubmitForm } from '@restspace/schema-form';

const apiIconMap = {
    urlFile: urlFileIcon,
    jsonData: jsonFileIcon,
    authentication: authenticationIcon,
    services: servicesIcon,
    process: processIcon,
    payments: paymentsIcon,
    overlay: overlayIcon,
    zipUpload: overlayIcon
};

enum EditorState {
    List, Choose, Add, Edit
}

export const ServicesEditor = (props: IEditorProps) => {
    const {
        path, mimeType, onDirty, onDirChange, text, operation, options, service
    } = props;
    
    const [ services, setServices ] = useState<ServiceConfig | null>(null);
    const [ editorState, setEditorState ] = useState(EditorState.List);
    const [ editService, setEditService ] = useState<object | null>(null);
    const [ editBasePath, setEditBasePath ] = useState<string | null>(null);
    const [ editSchema, setEditSchema ] = useState<object | null>(null);
    const [ isDirty, setIsDirty ] = useState(false);
    const [ dataDirty, setDataDirty ] = useState(false);

    const details = config.serviceCatalogue.serviceDetails;
    const adapterDetails = config.serviceCatalogue.adapterDetails;
    const baseSchema = config.serviceCatalogue.baseSchema;
    const expandSchema = (serviceSchema: any, service: object | null) => {
        const expandedSchema = {
            ...serviceSchema,
            type: "object",
            properties: {
                ...baseSchema.properties,
                ...serviceSchema.properties
            },
            required: [ ...(baseSchema.required || []), ...(serviceSchema.required || []) ]
        };
        const adapterName = expandedSchema.properties['adapterName'];
        if (adapterName && service) {
            // filter adapters in dropdown to those with right interface for service
            const adInterface = details[service['serviceName'] as string].adapterInterface;
            adapterName.enum = Object.keys(adapterDetails).filter((ad: string) => adapterDetails[ad].interfaces.includes(adInterface));
        }
        if (expandedSchema.properties['adapterConfig'] && service && service['adapterName']) {
            expandedSchema.properties['adapterConfig'] = config.serviceCatalogue.adapterDetails[service['adapterName']].schema;
        }
        return expandedSchema;
    };
    const setDirty = (newDirty: boolean) => {
        if (newDirty && !isDirty) {
            setIsDirty(true);
            setDataDirty(true);
            onDirty(true);
        } else if (!newDirty && isDirty) {
            setIsDirty(false);
            setDataDirty(false);
            onDirty(false);
        }
    }

    useEffect(() => {
        if (text) {
            setServices(JSON.parse(text));
            return;
        }

        config.api.fetchText(path)
        .then(([text]) => {
            const obj = JSON.parse(text);
            setServices(obj);
        })
        .catch((err) => {
            alert('loading code failed');
            console.log('loading code failed: ' + err);
        });

        //onDoDirty(false);
    }, [ path, text ]);

    
    // Event Handlers

    const onFormDirty = (formIsDirty: boolean) => {
        if (formIsDirty && !isDirty) {
            setIsDirty(true);
            onDirty(true);
        }
    };
    const onFormSubmit = async (serviceValue: any) => {
        if (editorState === EditorState.Add || (editorState === EditorState.Edit && editBasePath !== null)) {
            const basePath = serviceValue.basePath;
            delete serviceValue.basePath;
            setServices(services => {
                if (services == null) return null;
                
                const newServices = { ...services.services };
                if (editBasePath) delete newServices[editBasePath];
                newServices[basePath] = serviceValue;
                return { ...services, services: newServices };
            });
            setEditorState(EditorState.List);
            setDataDirty(true);
            setDirty(true);
        }
        return true;
    };

    const onFormChange = (newValue: any, path: string[]) => {
        if (path[0] === 'adapterName') {
            if (editSchema && editSchema['properties']['adapterConfig']) {
                editSchema['properties']['adapterConfig'] = config.serviceCatalogue.adapterDetails[newValue.adapterName].schema;
                setEditSchema(editSchema);
            }
        }
    }

    const actionButtonTransition = () => {
        if (editorState !== EditorState.List && !dataDirty && isDirty) { // cancelling but no prior data change
            setIsDirty(false);
            onDirty(false);
        }
        setEditorState(state => state === EditorState.List ? EditorState.Choose : EditorState.List);
    }
    const createService = (service: ServiceDetail) => () => {
        const newService = {
            serviceName: service.name
        };
        setEditService(newService);
        const serviceSchema = service.schema;
        const schema = expandSchema(serviceSchema, newService);
        setEditSchema(schema);
        setEditorState(EditorState.Add);
    }
    const editAction = (basePath: string) => () => {
        if (services === null) return;
        setEditorState(EditorState.Edit);
        const service = { ...services.services[basePath], basePath };
        setEditService(service);
        setEditSchema(expandSchema(details[service.serviceName].schema, service));
        setEditBasePath(basePath);
    }
    const deleteAction = (basePath: string) => () => {
        if (services === null) return;
        if (!window.confirm('Are you sure you want to delete this service? In some cases this will delete any resources or external service accounts associated with it.')) {
            return;
        }

        setServices(services => {
            if (services !== null) {
                delete services.services[basePath];
                return { ...services } as ServiceConfig;
            }
            return services;
        });
        setDirty(true);
    }

    const commitChanges = async () => {
        if (!window.confirm("Do you want to apply these changes to the site?")) return;

        let submitText = JSON.stringify(services);
        const res = await config.api.saveItem(path, submitText, 'application/json');
        if (res) {
            alert(res);
        }
        // reload everything for services as the list of services may have changed
        document.location.reload();
    }

    // Precalculation

    const servicePanelClasses = `service-panel-area ${editorState === EditorState.List ? 'closed' : 'open'}`;
    const getIcons = (apisSupported: string) => {
        const icons = apisSupported.split(' ').filter(api => !!api).map(api => apiIconMap[api]);
        return icons.map(icon => <img key={icon} className='api' src={icon}/>);
    };
    const serviceGroups = _.groupBy(Object.values(details), (detail) => detail.apisSupported);

    // Service list

    const servicesList = (services: { [basePath: string]: ServiceFull }) => 
        Object.entries(services)
            .sort(([a], [b]) => a === b ? 0 : (a > b ? 1 : -1))
            .map(([basePath, spec], idx) => {
            return (
                <div key={spec.name} className="service-row">
                    <div className="base-path">
                        {basePath}
                    </div>
                    <div className="service-block">
                        <div className="top-bar">
                            {details[spec.serviceName] && getIcons(details[spec.serviceName].apisSupported)}
                            <div className="service">{spec.serviceName.toUpperCase()}</div>
                            <div className="service-name" onClick={editAction(basePath)}>{spec.name}</div>
                            <span className="sf-control-button sf-delete-button" onClick={deleteAction(basePath)}></span>
                            <span className="sf-control-button sf-edit-button" onClick={editAction(basePath)}></span>
                        </div>
                        {details[spec.serviceName] &&
                            <div className="documentation">{details[spec.serviceName].documentation}</div>}
                        <div className="roles">
                            <div className="read">{spec.readRoles}</div>
                            <div className="write">{spec.writeRoles}</div>
                            {spec.createRoles && <div className="create">{spec.createRoles}</div>}
                        </div>
                    </div>
                </div>
            );
        });

    const serviceSelector = (serviceGroups: _.Dictionary<ServiceDetail[]>) => 
        Object.entries(serviceGroups).map(([apisSupported, services]) => {
            return (
                <>
                <div className="api-services">
                    {getIcons(apisSupported)}
                    <h2 className='api-title'>{apisSupported}</h2>
                </div>
                <div className="service-group">
                    {services.map(service => (
                        <div key={service.name} className="service-button" onClick={createService(service)}>
                            <h3>{service.name}</h3>
                            <div>
                                {service.documentation}
                            </div>
                            <a className="doc-link" href={`https://restspace.io/docs/services/${service.name}`} target="_blank">Documentation</a>
                        </div>
                    ))}
                </div>
                </>
            );
        });

    const makeSubmitLink = (onClick: () => void) => {
        return (
        <div className="jd-submit-button-container">
            <button type="button" className="jd-submit-button" onClick={onClick}>
            Submit
            </button>
        </div>
        );
    }
    
    const serviceEditor = () => {
        if (editService === null || editSchema === null) return <></>;
        return (
            <div>
                <SchemaSubmitForm
                    schema={editSchema}
                    value={editService}
                    onDirty={onFormDirty}
                    onChange={onFormChange}
                    onSubmit={onFormSubmit}
                    makeSubmitLink={makeSubmitLink}
                />
            </div>
        );
    }

    const actionButton = () => {
        switch (editorState) {
            case EditorState.List:
                return (
                <>
                    <div className="action-button">
                        <span className="sf-control-button sf-add-button" onClick={actionButtonTransition}>+</span>
                        <span>Add service</span>
                    </div>
                    {dataDirty && <div className="action-button">
                        <span className="sf-control-button sf-save-button" onClick={commitChanges}>s</span>
                        <span>Commit changes</span>
                    </div>}
                </>);
            default:
                return (
                <>
                    <div className="action-button">
                        <span className="sf-control-button sf-delete-button" onClick={actionButtonTransition}>x</span>
                        <span>Cancel</span>
                    </div>
                </>);
        }
    }

    return (
        <div className="services-editor">
            <div className="action">
                {actionButton()}
            </div>
            <div className="services-container">
                <div className="services-list-area">
                    <div className="services-list-panel">
                        {services && servicesList(services.services)}
                    </div>
                </div>
                <div className={servicePanelClasses}>
                    <div className="service-panel">
                        {editorState === EditorState.Choose
                            ? serviceSelector(serviceGroups)
                            : serviceEditor()}
                    </div>
                </div>
            </div>
        </div>
    );
}